import {
  AppointmentCreator,
  AppointmentState,
  AppointmentStatus
} from 'types/api/appointments';
import { FastAppointmentStatus } from 'types/api/fastAppointments';

// Phase E Cycle 1 — values sourced from the canonical BE enum.
export const APPOINTMENT_STATUSES = {
  awaiting_confirmation: AppointmentStatus.AWAITING_CONFIRMATION,
  approved: AppointmentStatus.APPROVED,
  started: AppointmentStatus.STARTED,
  completed: AppointmentStatus.COMPLETED,
  canceled: AppointmentStatus.CANCELED,
  rejected: AppointmentStatus.REJECTED,
  finished: AppointmentStatus.FINISH
} as const satisfies Record<string, AppointmentStatus>;

export const APPOINTMENT_STATUS_VALUES = {
  [AppointmentStatus.AWAITING_CONFIRMATION]: 'awaiting_confirmation',
  [AppointmentStatus.APPROVED]: 'approved',
  [AppointmentStatus.STARTED]: 'started',
  [AppointmentStatus.COMPLETED]: 'completed',
  [AppointmentStatus.CANCELED]: 'canceled',
  [AppointmentStatus.REJECTED]: 'rejected',
  [AppointmentStatus.FINISH]: 'finished'
} as const satisfies Record<AppointmentStatus, string>;

export type AppointmentStatusType = AppointmentStatus;

// CREATORS — also used as `sender` on appointment dates/files/notes.
export const APPOINTMENT_CREATOR = {
  admin: AppointmentCreator.Admin,
  patient: AppointmentCreator.Patient,
  doctor: AppointmentCreator.Doctor
} as const satisfies Record<string, AppointmentCreator>;

export const ADMIN_PAID = [
  { value: '0', label: 'Not Paid' },
  { value: '1', label: 'Paid' }
];

// STATE
export const APPOINTMENT_STATE_PENDING_VALUE = AppointmentState.PENDING;
export const APPOINTMENT_STATE_APPROVED_VALUE = AppointmentState.APPROVED;
export const APPOINTMENT_STATE_REJECTED_VALUE = AppointmentState.REJECTED;
export const APPOINTMENT_STATE_MODERATE_VALUE = AppointmentState.MODERATE;

export const APPOINTMENT_STATE_PENDING_LABEL = 'Pending';
export const APPOINTMENT_STATE_APPROVED_LABEL = 'Approved';
export const APPOINTMENT_STATE_REJECTED_LABEL = 'Rejected';
export const APPOINTMENT_STATE_MODERATE_LABEL = 'Moderate';

export const APPOINTMENT_STATES = {
  [AppointmentState.PENDING]: APPOINTMENT_STATE_PENDING_LABEL,
  [AppointmentState.APPROVED]: APPOINTMENT_STATE_APPROVED_LABEL,
  [AppointmentState.REJECTED]: APPOINTMENT_STATE_REJECTED_LABEL,
  [AppointmentState.MODERATE]: APPOINTMENT_STATE_MODERATE_LABEL
} as const;

export const APPOINTMENT_STATES_COLOR = {
  [AppointmentState.PENDING]: 'yellow',
  [AppointmentState.APPROVED]: 'green',
  [AppointmentState.REJECTED]: 'red',
  [AppointmentState.MODERATE]: 'yellow'
} as const;

export type AppointmentStateType = AppointmentState;

// Phase E Cycle 3 — FastAppointment canonical (BE).
export const FAST_APPOINTMENTS_STATUS = {
  new: FastAppointmentStatus.New,
  replied: FastAppointmentStatus.Replied,
  in_process: FastAppointmentStatus.Pending,
  rejected: FastAppointmentStatus.Rejected,
  succeed: FastAppointmentStatus.Succeed,
  canceled: FastAppointmentStatus.Canceled
} as const satisfies Record<string, FastAppointmentStatus>;

export const FAST_APPOINTMENTS_COLOR = {
  [FastAppointmentStatus.New]: '#FF5733',
  [FastAppointmentStatus.Replied]: '#2196F3',
  [FastAppointmentStatus.Rejected]: '#E71D36',
  [FastAppointmentStatus.Succeed]: '#36A64F',
  [FastAppointmentStatus.Canceled]: '#777777',
  [FastAppointmentStatus.Pending]: '#faab1e'
} as const;

export const FAST_APPOINTMENTS_LABEL = {
  [FastAppointmentStatus.New]: 'New',
  [FastAppointmentStatus.Replied]: 'Replied',
  [FastAppointmentStatus.Rejected]: 'Rejected',
  [FastAppointmentStatus.Succeed]: 'Succeed',
  [FastAppointmentStatus.Canceled]: 'Canceled',
  [FastAppointmentStatus.Pending]: 'In process'
} as const;

export type FastAppointmentValues = FastAppointmentStatus;

// MedicalTreatment is the admin workflow view over the BE
// `treatment_forms` table (POST/PUT uses updateTreatmentForm).
// The BE TreatmentFormStatus enum has only 3 canonical values
// (new / reviewed / closed); the admin UI historically rendered 7
// workflow states. These admin-only canonical strings stay for
// label / color routing — wire reads from BE come back as one of
// TreatmentFormStatus, the lookup tolerates unknown keys.
export const MEDICAL_TREATMENT_STATUS = {
  new: 'new',
  contacted: 'contacted',
  in_process: 'in_process',
  visa_invitation_sent: 'visa_invitation_sent',
  completed: 'completed',
  canceled: 'canceled',
  waiting_for_arrival: 'waiting_for_arrival'
} as const;

export const MEDICAL_TREATMENT_COLOR = {
  [MEDICAL_TREATMENT_STATUS.new]: '#FF5733',
  [MEDICAL_TREATMENT_STATUS.contacted]: '#2196F3',
  [MEDICAL_TREATMENT_STATUS.visa_invitation_sent]: '#E71D36',
  [MEDICAL_TREATMENT_STATUS.completed]: '#36A64F',
  [MEDICAL_TREATMENT_STATUS.canceled]: '#777777',
  [MEDICAL_TREATMENT_STATUS.in_process]: '#faab1e',
  [MEDICAL_TREATMENT_STATUS.waiting_for_arrival]: '#E633FF'
} as const;

export const MEDICAL_TREATMENT_LABEL = {
  [MEDICAL_TREATMENT_STATUS.new]: 'New',
  [MEDICAL_TREATMENT_STATUS.contacted]: 'Replied',
  [MEDICAL_TREATMENT_STATUS.visa_invitation_sent]: 'Visa Invitation Sent',
  [MEDICAL_TREATMENT_STATUS.completed]: 'Completed',
  [MEDICAL_TREATMENT_STATUS.canceled]: 'Canceled',
  [MEDICAL_TREATMENT_STATUS.in_process]: 'In process',
  [MEDICAL_TREATMENT_STATUS.waiting_for_arrival]: 'Waiting for Arrival'
} as const;

export type MedicalTreatmentValues =
  (typeof MEDICAL_TREATMENT_STATUS)[keyof typeof MEDICAL_TREATMENT_STATUS];
